import React, { MutableRefObject, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Nullable, SelectOption } from 'global';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField, SelectField, CheckboxField } from 'components/form';

import { Template } from '../../../api';
import { stagesMapper } from '../../../mappers';
import { TemplateFormValues } from '../../../types';
import { TemplateFormValuesSchema } from '../../../model';
import { initialStagesConstructorFormValue } from '../../../constants';

interface Props {
  isDepartmentsLoading: boolean;
  template: Nullable<Template>;
  departmentsOptions: SelectOption[];
  submitRef: MutableRefObject<{ submit: () => Promise<TemplateFormValues | null> } | null>;
}

export const StagesConstructorForm = ({ submitRef, template, departmentsOptions, isDepartmentsLoading }: Props) => {
  const { control, getValues, trigger } = useForm<TemplateFormValues>({
    mode: 'onChange',
    resolver: zodResolver(TemplateFormValuesSchema),
    defaultValues: template ? stagesMapper.processTemplateToFormValues(template) : initialStagesConstructorFormValue,
  });

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await trigger();
      return isValid ? getValues() : null;
    },
  }));

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
      <InputField
        control={control}
        name="templateName"
        label="Template Name"
        inputProps={{ required: true, variant: 'outlined' }}
      />

      <SelectField
        required
        control={control}
        variant="outlined"
        label="Department"
        name="businessProcess"
        disabled={Boolean(template)}
        options={departmentsOptions}
        isLoading={isDepartmentsLoading}
      />

      {!template && <CheckboxField name="isActive" control={control} label="Make template active" />}
    </Box>
  );
};
